/**
 * Type Definitions
 */

import { Reducer } from 'react'
import { Axios } from '../../services/axios'
import { USER_TYPE } from '../../utils/constants/enum'
import { EndeavorAuthType, UserDetailsTypes } from '../../utils/types/auth'
import { SocialLink } from '../../utils/types/ContentStack'
import jwt_decode from 'jwt-decode'
import { DecodedJWT } from '../../utils/types/common'
import { WORKOUT_LEVELS } from '../../utils/constants'
import { BucketItem } from '../../utils/types/Endeavor'

export type Levels = 'beginner' | 'intermediate' | 'challenging'

export interface GlobalState {
    endeavorToken: EndeavorAuthType
    user: UserDetailsTypes
    isModalOpen: boolean
    socialLinks?: SocialLink
    userType: USER_TYPE
    isUserAddingCard: boolean
    isPromotionVisible: boolean
    weeklySchedule: {
        beginner: Array<BucketItem>
        intermediate: Array<BucketItem>
        challenging: Array<BucketItem>
    }
    activeLevel: Levels
}

/**
 * Initial State
 */

export const initialGlobalState: GlobalState = {
    endeavorToken: {
        authorisationToken: '',
        refreshToken: '',
    },
    user: {
        id: null,
        contactEmail: '',
        phoneNumber: '',
        preferredLocale: '',
        name: {
            fullName: '',
            preferredName: '',
        },
        exid: '',
    },
    isModalOpen: false,
    socialLinks: undefined,
    userType: USER_TYPE.REGULAR,
    isUserAddingCard: false,
    weeklySchedule: {
        beginner: [],
        intermediate: [],
        challenging: [],
    },
    activeLevel: WORKOUT_LEVELS[0] as Levels,
    isPromotionVisible: true,
}

export const MIN_DISTANCE = 5

/**
 * Action Definitions
 */

type SetWeeklySchedule = {
    type: 'SET_WEEKLY_SCHEDULE'
    payload: {
        level: Levels
        data: Array<BucketItem>
    }
}

type SetActiveLevel = {
    type: 'SET_ACTIVE_LEVEL'
    payload: Levels
}

type SetTokenAction = {
    type: 'SET_ENDEAVOR_TOKEN'
    payload: EndeavorAuthType
}

type SetUserData = {
    type: 'SET_USER_DATA'
    payload: UserDetailsTypes
}

type SetGuestUserRegModal = {
    type: 'SET_GUEST_USER_REGISTRATION_MODAL'
    payload: boolean
}

type setSocialLinks = {
    type: 'SET_SOCIAL_LINKS'
    payload: SocialLink
}

type TogglePromoBanner = {
    type: 'TOGGLE_PROMOTION'
    payload: boolean
}

type GlobalActions =
    | SetTokenAction
    | SetUserData
    | SetGuestUserRegModal
    | setSocialLinks
    | TogglePromoBanner
    | SetWeeklySchedule
    | SetActiveLevel

/**
 * Reducer Definition
 */

export const globalReducer: Reducer<GlobalState, GlobalActions> = (
    state,
    action
) => {
    switch (action.type) {
        case 'SET_ENDEAVOR_TOKEN':
            Axios.setEndeavorAuthToken(action.payload)
            let userType = USER_TYPE.REGULAR
            if (action.payload.authorisationToken) {
                const decoded: DecodedJWT = jwt_decode(
                    action.payload.authorisationToken
                )
                if (decoded?.dap) {
                    const dap = decoded.dap.toString(2)
                    const vipFlag = dap.charAt(dap.length - 1)
                    if (vipFlag === '1') {
                        userType = USER_TYPE.VIP
                    }
                }
            }
            return {
                ...state,
                endeavorToken: action.payload,
                userType: userType,
            }
        case 'SET_USER_DATA':
            const userExid: DecodedJWT = jwt_decode(
                state.endeavorToken.authorisationToken
            )
            return { ...state, user: { ...action.payload, exid: userExid.sub } }
        case 'SET_GUEST_USER_REGISTRATION_MODAL':
            return { ...state, isModalOpen: action.payload }
        case 'SET_SOCIAL_LINKS':
            return { ...state, socialLinks: action.payload }
        case 'TOGGLE_PROMOTION':
            return { ...state, isPromotionVisible: action.payload }
        case 'SET_WEEKLY_SCHEDULE':
            const activeLevel = state.activeLevel
                ? state.activeLevel
                : (WORKOUT_LEVELS[0] as Levels)
            return {
                ...state,
                weeklySchedule: {
                    ...state.weeklySchedule,
                    [action.payload.level]: action.payload.data,
                },
                activeLevel,
            }
        case 'SET_ACTIVE_LEVEL':
            return { ...state, activeLevel: action.payload }
    }
}
